import { format } from "date-fns";
import { AlertTriangle, ArrowRight, Check, Loader2, Users, X } from "lucide-react";
import { useState } from "react";
import { useAutoAssignPreview, useAutoAssignTopics } from "@/hooks/admin/assignments";
import { cn } from "@/lib/utils";

interface AutoAssignModalProps {
  open: boolean;
  onClose: () => void;
  assignmentId: string;
  weekNumber: number;
  assignmentNumber: number;
  onSuccess: () => void;
}

export function AutoAssignModal({
  open,
  onClose,
  assignmentId,
  weekNumber,
  assignmentNumber,
  onSuccess,
}: AutoAssignModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);

  const previewQuery = useAutoAssignPreview(assignmentId, open);
  const { mutateAsync: autoAssignTopics, isPending: isAssigning } = useAutoAssignTopics();

  const isLoading = previewQuery.isPending && open;
  const preview = previewQuery.data ?? null;
  const error =
    submitError ??
    (previewQuery.isError
      ? previewQuery.error instanceof Error
        ? previewQuery.error.message
        : "Failed to load preview"
      : null);

  const handleAssign = async () => {
    setSubmitError(null);
    try {
      await autoAssignTopics({ assignmentId });
      onSuccess();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Failed to assign topics");
    }
  };

  if (!open) return null;

  const canAssign =
    preview &&
    preview.assignments.length > 0 &&
    preview.assignments.length <= preview.ready_topics_available;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Auto-Assign Topics</h2>
            <p className="text-sm text-gray-500 mt-1">
              Week {weekNumber} - Assignment {assignmentNumber}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
              {error}
            </div>
          ) : preview ? (
            <div className="space-y-6">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Users Need Topics</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {preview.users_need_topics}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Ready Topics Available</div>
                    <div
                      className={cn(
                        "text-lg font-semibold",
                        preview.ready_topics_available >= preview.users_need_topics
                          ? "text-green-600"
                          : "text-red-600"
                      )}
                    >
                      {preview.ready_topics_available}
                    </div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-gray-500 text-sm">Priority</div>
                  <div className="text-gray-700">Oldest topics first (FIFO)</div>
                </div>
              </div>

              {/* Preview List */}
              {preview.assignments.length > 0 ? (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {preview.assignments.map((item, index) => (
                      <div
                        key={`${item.user_id}-${item.topic_id}-${index}`}
                        className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{item.username}</div>
                          <div className="text-sm text-gray-600 truncate">{item.topic_title}</div>
                          <div className="text-xs text-gray-400 mt-1">
                            Created {format(new Date(item.topic_created_at), "MMM d, yyyy")}
                          </div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>All users already have topics assigned</p>
                </div>
              )}

              {/* Not enough topics warning */}
              {preview.users_need_topics > preview.ready_topics_available && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-medium text-amber-800">Not Enough Topics</div>
                    <div className="text-sm text-amber-700 mt-1">
                      You need {preview.users_need_topics - preview.ready_topics_available} more
                      ready topics to assign to all users.
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            disabled={isAssigning}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!canAssign || isAssigning}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 disabled:bg-primary/40 text-white rounded-lg transition-colors"
          >
            {isAssigning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Confirm Assignment
          </button>
        </div>
      </div>
    </div>
  );
}
